import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export interface Campaign {
  id: string;
  name: string;
  channel?: string | null;
  status: string;
  segmentId: string | null;
  templateId?: string | null;
  settings?: {
    fromEmail?: string;
    fromName?: string;
    replyTo?: string;
    subjectOverride?: string;
  } | null;
  startedAt?: string | null;
  completedAt?: string | null;
  scheduledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useCampaigns(status?: string) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const params = new URLSearchParams({ limit: "50" });
    if (status) params.set("status", status);

    try {
      const res = await apiFetch<{ data: Campaign[] }>(
        `/crm/campaigns?${params.toString()}`,
      );
      setCampaigns(res.data ?? []);
    } catch (err) {
      setCampaigns([]);
      setError(err instanceof ApiError ? err.message : "Failed to load campaigns");
    } finally {
      setIsLoading(false);
    }
  }, [status]);

  useEffect(() => { fetch(); }, [fetch]);
  return { campaigns, isLoading, error, refetch: fetch };
}

export async function createCampaign(data: {
  name: string;
  subject?: string;
  segmentId?: string;
  templateId?: string;
}): Promise<Campaign> {
  const res = await apiFetch<{ data: Campaign }>("/crm/campaigns", {
    method: "POST",
    body: JSON.stringify({
      name: data.name,
      segmentId: data.segmentId,
      templateId: data.templateId,
      settings: data.subject ? { subjectOverride: data.subject } : undefined,
    }),
  });
  return res.data;
}

export async function updateCampaign(id: string, data: Record<string, unknown>): Promise<void> {
  await apiFetch(`/crm/campaigns/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function scheduleCampaign(id: string, scheduledAt: string): Promise<void> {
  await apiFetch(`/crm/campaigns/${id}/schedule`, {
    method: "POST",
    body: JSON.stringify({ scheduledAt }),
  });
}

export async function sendCampaign(id: string): Promise<void> {
  await apiFetch(`/crm/campaigns/${id}/send`, {
    method: "POST",
  });
}
